import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';
import { ringLabel, isRing } from '@theme/r69-ring-sizes';

/*
 * ROTAS69 "Pairing" — the cross-sell / editorial discovery band (ported from
 * FAORIA `faoria-pairing`, adapted to Horizon 4.1.1 + ROTAS69):
 *   - complementary present -> "Complete the look" board + live bundle
 *     ("Add the look" = current product + ticked pieces in ONE cart POST) AND
 *     the related "More to love" edit below it;
 *   - no complementary -> only the related "More to love".
 *
 * Differences from FAORIA: cart adds go through Horizon's CartLinesUpdateEvent
 * (deferred promise) so the native cart drawer opens/refreshes; cart-awareness
 * listens to StandardEvents.cartLinesUpdate + re-fetches /cart.js; the hero re-syncs
 * on StandardEvents.productSelect by re-reading the live [name="id"]. The material
 * line is a static product metafield (custom.parklajums, else shopify.jewelry-material),
 * so there is NO colour->material remap — only price/image/size/availability update per variant.
 * UI strings come from the section's `data-r69pair-i18n` blob (LV/RU/LT/ET).
 */

const MONEY_DEFAULT = '{{amount}}';

const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
));

const eqv = (a, b) => String(a == null ? '' : a).toLowerCase().trim() === String(b == null ? '' : b).toLowerCase().trim();

const DEFAULT_I18N = {
  inbag: 'Jau grozā', soldout: 'Izpārdots', sale: 'Akcija', save: 'Kopā ietaupi {amount}',
  add: 'Pievienot komplektu', addPrice: 'Pievienot komplektu — {amount}',
  allInBag: 'Viss jau ir grozā', complete: 'komplekts', piece: 'prece', pieces: 'preces',
  placeholder: 'Šeit parādīsies ieteiktie izstrādājumi.',
};

class R69Pairing extends HTMLElement {
  connectedCallback() {
    if (this._init) return;
    this._init = true;

    this.moneyFormat = this.dataset.moneyFormat || MONEY_DEFAULT;
    this.currency = this.dataset.currency || '';
    this.baseRing = this.dataset.baseRing === 'true';
    // the hero shows the current product's size — as Ø for rings, matching the main picker (cheap, keep instant)
    const heroSize0 = this.querySelector('[data-r69pair-hero-size]');
    if (heroSize0 && this.baseRing && this.dataset.baseSize) heroSize0.textContent = (heroSize0.dataset.prefix ? heroSize0.dataset.prefix + ': ' : '') + ringLabel(this.dataset.baseSize);

    // PERF: defer the heavy init (cart fetch, skins, related fetch+build) OFF the boot window — the
    // first seconds after load are already saturated (Judge/apps boot) and that's exactly when the
    // shopper starts scrolling. The server-rendered skeletons hold the layout meanwhile.
    const start = () => this.#start();
    if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 3500 });
    else setTimeout(start, 800);
  }

  #start() {
    this.productId = String(this.dataset.productId || '');
    this.baseVid = this.dataset.baseVid || '';
    this.basePc = parseInt(this.dataset.basePc || '0', 10);
    this.baseCc = parseInt(this.dataset.baseCc || this.dataset.basePc || '0', 10);
    this.baseSize = this.dataset.baseSize || '';

    this.i18n = this.#readJSON('[data-r69pair-i18n]', DEFAULT_I18N);
    this.lookRegion = this.querySelector('[data-region="look"]');
    this.editRegion = this.querySelector('[data-region="edit"]');
    // The rule between the two regions. It may only show when BOTH regions are on screen,
    // so it is toggled wherever the related region is.
    this.ruleEl = this.querySelector('[data-r69pair-rule]');
    this.itemsEl = this.querySelector('[data-r69pair-items]');
    this.editGridEl = this.querySelector('[data-r69pair-editgrid]');

    this.cartSet = new Set();
    // Drag-to-scroll comes from the shared module (assets/r69-drag-scroll.js), never a local copy.
    // The copy that used to live here listened to MOUSE events, and a phone sends compatibility
    // mouse events after every tap: mousedown, then a mousemove a few pixels away because a finger
    // is not a mouse, then click. Those few pixels tripped its drag threshold, so it scrolled the
    // rail and cancelled the click — the whole row stopped opening products on touch, while a real
    // mouse worked. The shared module binds POINTER events and returns immediately unless
    // pointerType is 'mouse', so touch never reaches it. Proven side by side: the same synthetic
    // tap sequence is killed by the old code and survives on a rail wired by the module.
    window.r69DragScroll?.(this);
    this.#bindBaseVariant();
    this.#initLook();        // complementary are server-rendered; paint the cached cart state (no flicker)
    this.#refreshCart();     // confirm against the live cart
    document.addEventListener(StandardEvents.cartLinesUpdate, (e) => this.#onCartEvent(e));
    document.addEventListener('visibilitychange', () => { if (!document.hidden) this.#refreshCart(); });
    window.addEventListener('pageshow', (e) => { if (e.persisted) this.#refreshCart(); });
    window.addEventListener('storage', (e) => { if (e.key === 'r69_pairing_cart') this.#refreshCart(); });
    this.#load();            // related from the recommendations API
  }

  // Only when the complementary board above it is actually rendered AND the related grid
  // below it survived — otherwise the rule would float with nothing on one side.
  #syncRule() {
    if (!this.ruleEl) return;
    const lookOn = !!this.lookRegion && !this.lookRegion.hidden;
    const editOn = !!this.editRegion && !this.editRegion.hidden;
    this.ruleEl.hidden = !(lookOn && editOn);
  }

  #readJSON(sel, fallback) {
    const el = this.querySelector(sel);
    if (!el) return fallback;
    try { return { ...fallback, ...JSON.parse(el.textContent.trim()) }; } catch (e) { return fallback; }
  }

  async #onCartEvent(e) {
    if (e && e.promise) { try { await e.promise; } catch (err) { /* ignore */ } }
    this.#refreshCart();
  }

  async #fetchIntent(intent) {
    const url = `${this.dataset.recoUrl}&product_id=${this.productId}&section_id=${this.dataset.sectionId}&intent=${intent}`;
    try {
      const res = await fetch(url);
      if (!res.ok) return [];
      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const el = doc.querySelector('[data-r69pair-json]');
      if (!el) return [];
      const data = JSON.parse(el.textContent.trim());
      return Array.isArray(data) ? data.filter((p) => p && String(p.id) !== this.productId) : [];
    } catch (e) {
      return [];
    }
  }

  async #load() {
    const related = await this.#fetchIntent('related');
    const seen = new Set([this.productId, ...(this.compIds || [])]);
    const rel = [];
    for (const p of related) {
      if (seen.has(String(p.id))) continue;
      seen.add(String(p.id));
      rel.push(p);
      if (rel.length >= 10) break;
    }
    if (rel.length) {
      this.#renderEdit(rel);
    } else {
      // no related products: drop the reservation skeletons and collapse the region (designMode shows a hint)
      this.editGridEl.querySelectorAll('.r69pair__card--skel').forEach((el) => el.remove());
      if (window.Shopify && Shopify.designMode && (!this.lookItems || !this.lookItems.length)) {
        this.editGridEl.insertAdjacentHTML('beforeend', `<p class="r69pair__placeholder">${esc(this.i18n.placeholder)}</p>`);
      } else {
        this.editRegion.hidden = true;
        this.#syncRule();
      }
    }
    this.setAttribute('data-ready', 'true');
  }

  /* ---------- Complete the look (complementary — server-rendered) ---------- */
  #initLook() {
    this.lookItems = Array.from(this.querySelectorAll('.r69pair__item'));
    this.compIds = new Set(this.lookItems.map((el) => el.dataset.pid).filter(Boolean));
    if (!this.lookItems.length) return;

    this.lookItems.forEach((item) => {
      const data = item.querySelector('.r69pair__vardata');
      try { item._vars = data ? JSON.parse(data.textContent.trim()) : []; } catch (e) { item._vars = []; }
      item._cpos = parseInt(item.dataset.cpos || '0', 10);
      item._spos = parseInt(item.dataset.spos || '0', 10);
      item._sname = item.dataset.sname || '';

      const cb = item.querySelector('.r69pair__cb');
      if (cb) cb.addEventListener('change', () => this.#recalc());
      const sizeSel = item.querySelector('.r69pair__size');
      if (sizeSel) sizeSel.addEventListener('change', () => this.#onSizeChange(item, sizeSel));

      // relabel the server-rendered ring sizes to "Izmērs: Ø …" (match the main PDP picker)
      if (sizeSel && item._vars.length) {
        const shown = Array.from(sizeSel.options)
          .map((o) => { const v = item._vars.find((x) => String(x.id) === String(o.value)); return v ? v.s : null; })
          .filter((x) => x != null);
        if (isRing(shown)) {
          Array.from(sizeSel.options).forEach((o) => {
            const v = item._vars.find((x) => String(x.id) === String(o.value));
            if (v) o.textContent = this.#optLabel(v.s, true, item._sname);
          });
        }
        this.#buildSkin(sizeSel); // desktop custom dropdown over the native select
      }
    });

    this.addBtn = this.querySelector('[data-r69pair-add]');
    if (this.addBtn) this.addBtn.addEventListener('click', () => this.#addLook());
    this.#matchComplementary(this.baseSize);
    this.#applyCachedCart();
    this.#recalc();
  }

  // Offer each complementary piece in the main product's size IF that exact size
  // exists for it and is available; otherwise leave the piece untouched.
  #matchComplementary(mainSize) {
    if (!this.lookItems || !mainSize) return;
    this.lookItems.forEach((item) => {
      const vars = item._vars;
      if (!vars || !vars.length || !item._spos) return;
      const match = vars.find((v) => v.avail && eqv(v.s, mainSize));
      if (!match || String(item.dataset.vid) === String(match.id)) return;
      this.#applyItemVariant(item, match, true);
    });
    this.#recalc();
  }

  #applyItemVariant(item, v, rebuild) {
    item.dataset.vid = v.id;
    item.dataset.pc = v.pc; item.dataset.cc = v.cc;
    item.dataset.size = v.s == null ? '' : v.s;
    const priceEl = item.querySelector('[data-r69pair-price]');
    if (priceEl) priceEl.innerHTML = v.sale ? `<b class="r69pair__now">${esc(v.price)}</b> <s>${esc(v.cmp)}</s>` : `<b>${esc(v.price)}</b>`;
    const thumb = item.querySelector('[data-r69pair-thumb]');
    if (thumb && v.img && thumb.getAttribute('src') !== v.img) {
      thumb.style.opacity = '0';
      const pre = new Image();
      pre.onload = pre.onerror = () => { thumb.src = v.img; thumb.style.opacity = '1'; };
      pre.src = v.img;
    }
    const cb = item.querySelector('.r69pair__cb');
    if (cb) { cb.disabled = !v.avail; if (!v.avail) cb.checked = false; }
    item.classList.toggle('is-soldout', !v.avail);
    if (rebuild) this.#rebuildSize(item, v.id);
    this.#updateItemCart(item);
  }

  // Option label: rings → "Izmērs: Ø 18.1 mm" (option name + Ø), else "{sname}: {value}".
  #optLabel(sizeValue, ring, sname) {
    if (ring) return (sname ? sname + ': ' : '') + ringLabel(sizeValue);
    return sname ? `${sname}: ${sizeValue}` : String(sizeValue == null ? '' : sizeValue);
  }

  // Desktop "skin" over a native <select>: same look, sold-out sizes struck through.
  // The native <select> stays the source of truth (shown on mobile); on desktop CSS
  // hides it and shows this. A pick just drives the select (value + change event).
  #buildSkin(sel) {
    if (!sel) return;
    const wrap = sel.closest('.r69pair__sizewrap');
    if (!wrap) return;
    const old = wrap.querySelector('.r69pair__skin');
    if (old) old.remove();
    const opts = Array.from(sel.options);
    if (opts.length <= 1) return;
    const cur = sel.options[sel.selectedIndex] || sel.options[0];
    const optHTML = opts.map((o) =>
      `<button type="button" class="r69pair__skin-opt${o.disabled ? ' is-soldout' : ''}${o.selected ? ' is-selected' : ''}" role="option" data-vid="${esc(o.value)}"${o.disabled ? ' aria-disabled="true"' : ''}>${esc(o.textContent)}</button>`
    ).join('');
    const skin = document.createElement('div');
    skin.className = 'r69pair__skin';
    skin.innerHTML = `<button type="button" class="r69pair__skin-trigger" aria-haspopup="listbox" aria-expanded="false"><span class="r69pair__skin-cur">${esc(cur.textContent)}</span><span class="r69pair__skin-caret" aria-hidden="true"></span></button><div class="r69pair__skin-panel" role="listbox" hidden>${optHTML}</div>`;
    wrap.appendChild(skin);
    const trigger = skin.querySelector('.r69pair__skin-trigger');
    const panel = skin.querySelector('.r69pair__skin-panel');
    // Width-sync (owner request): the CLOSED trigger must already be as wide as the OPEN panel so
    // nothing jumps and no option text is ellipsized. The section is content-visibility-SKIPPED at
    // build time (below-fold) and every in-place geometry read returns 0 — the first attempt at this
    // measured in place and silently did nothing. Measure a CLONE parked on document.body instead
    // (the body is never skipped), then pin the trigger; CSS max-width:100% clamps it to the card.
    // Measure the PURE content width of the widest option on a body-parked clone (the section may be
    // content-visibility-skipped at build → in-place reads return 0). max-height:none + overflow:
    // visible so NO scrollbar distorts max-content (a scrolling clone under-measures: max-content
    // just tacks the scrollbar onto content, leaving options clipped). Then reserve 16px so the real
    // 8px scrollbar + margin fit. PLAIN px — NOT min(…,100%): the trigger is inline-flex with no
    // definite containing block, so % collapses to the tiny natural width and min() picks THAT,
    // truncating every option (proven live: min(105px,100%)→80px/25 cut; plain 105px→0 cut). The
    // .r69pair__skin + trigger max-width:100% is the real overflow guard.
    const probe = panel.cloneNode(true);
    probe.hidden = false;
    probe.style.cssText = 'position:absolute;left:-9999px;top:0;display:block;visibility:hidden;width:max-content;max-height:none;overflow:visible;';
    document.body.appendChild(probe);
    const contentW = Math.ceil(probe.getBoundingClientRect().width);
    probe.remove();
    if (contentW > 0) trigger.style.minWidth = (contentW + 16) + 'px';
    let open = false;
    const onDoc = (e) => { if (!skin.contains(e.target)) close(); };
    function close() {
      if (!open) return;
      open = false;
      panel.hidden = true;
      trigger.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', onDoc, true);
    }
    const openP = () => {
      if (open) return;
      open = true;
      panel.hidden = false;
      trigger.setAttribute('aria-expanded', 'true');
      // no open-time widening: the scrollbar width is baked into the trigger's min-width at build,
      // so the panel's width:100% already fits the options + scrollbar (open == closed, always)
      document.addEventListener('click', onDoc, true);
    };
    trigger.addEventListener('click', (e) => { e.preventDefault(); e.stopPropagation(); open ? close() : openP(); });
    panel.querySelectorAll('.r69pair__skin-opt').forEach((opt) => {
      opt.addEventListener('click', (e) => {
        e.preventDefault(); e.stopPropagation();
        if (opt.classList.contains('is-soldout')) return;
        close();
        sel.value = opt.dataset.vid;
        sel.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  }

  // keep the skin trigger + selected row in sync with the native select
  #updateSkinFromSelect(sel) {
    if (!sel) return;
    const wrap = sel.closest('.r69pair__sizewrap');
    const skin = wrap && wrap.querySelector('.r69pair__skin');
    if (!skin) return;
    const cur = sel.options[sel.selectedIndex];
    const curEl = skin.querySelector('.r69pair__skin-cur');
    if (curEl && cur) curEl.textContent = cur.textContent;
    skin.querySelectorAll('.r69pair__skin-opt').forEach((o) => o.classList.toggle('is-selected', o.dataset.vid === String(sel.value)));
  }

  // rebuild the size dropdown with UNIQUE sizes (no cross-colour dupes)
  #rebuildSize(item, selectedVid) {
    const sel = item.querySelector('.r69pair__size');
    if (!sel) return;
    const vars = item._vars || [];
    const color = item.dataset.color || '';
    const sname = item._sname || '';
    const seen = new Set();
    const rows = [];
    vars.forEach((v) => {
      if (item._cpos && color && !eqv(v.c, color)) return;
      if (seen.has(String(v.s))) return;
      seen.add(String(v.s));
      rows.push(v);
    });
    const ring = isRing(rows.map((v) => v.s));
    sel.innerHTML = rows.map((v) =>
      `<option value="${esc(v.id)}"${v.avail ? '' : ' disabled'}${String(v.id) === String(selectedVid) ? ' selected' : ''}>${esc(this.#optLabel(v.s, ring, sname))}</option>`
    ).join('');
    const wrap = sel.closest('.r69pair__sizewrap');
    if (wrap) wrap.style.display = seen.size > 1 ? '' : 'none';
    this.#buildSkin(sel);
  }

  #applyCachedCart() {
    try {
      const raw = localStorage.getItem('r69_pairing_cart');
      if (!raw) return;
      const arr = JSON.parse(raw);
      if (!Array.isArray(arr)) return;
      this.cartSet = new Set(arr.map(String));
      if (this.lookItems) this.lookItems.forEach((it) => this.#updateItemCart(it));
      this.#updateBaseCart();
      this.#updateEditCart();
      this.setAttribute('data-cart-ready', '');
    } catch (e) { /* ignore */ }
  }

  #onSizeChange(item, sel) {
    const opt = sel.options[sel.selectedIndex];
    if (!opt) return;
    const v = (item._vars || []).find((x) => String(x.id) === String(opt.value));
    if (!v) return;
    this.#applyItemVariant(item, v, false);
    this.#updateSkinFromSelect(sel);
    this.#recalc();
  }

  /* ---------- Cart awareness ---------- */
  async #fetchCart() {
    const url = ((window.Theme && Theme.routes && Theme.routes.cart_url) || '/cart') + '.js';
    const res = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('cart fetch failed');
    return res.json();
  }

  #applyCart(cart) {
    this.cartSet = new Set();
    (cart.items || []).forEach((it) => { this.cartSet.add(String(it.id)); if (it.variant_id) this.cartSet.add(String(it.variant_id)); });
    try { localStorage.setItem('r69_pairing_cart', JSON.stringify(Array.from(this.cartSet))); } catch (e) { /* ignore */ }
    if (this.lookItems) this.lookItems.forEach((it) => this.#updateItemCart(it));
    this.#updateBaseCart();
    this.#updateEditCart();
    this.setAttribute('data-cart-ready', '');
    this.#recalc();
  }

  async #refreshCart() {
    try { this.#applyCart(await this.#fetchCart()); }
    catch (e) {
      // keep last-known state, but stop hiding the check/in-bag UI
      this.setAttribute('data-cart-ready', '');
    }
  }

  #updateItemCart(item) {
    const inCart = this.cartSet.has(String(item.dataset.vid));
    item.classList.toggle('is-incart', inCart);
    const check = item.querySelector('.r69pair__check');
    const inbag = item.querySelector('.r69pair__inbag');
    if (check) check.hidden = inCart;
    if (inbag) inbag.hidden = !inCart;
  }

  #updateBaseCart() {
    const flag = this.querySelector('[data-r69pair-hero-cartflag]');
    if (flag) flag.hidden = !this.cartSet.has(String(this.#liveBaseVariant()));
  }

  #recalc() {
    const baseInCart = this.cartSet.has(String(this.#liveBaseVariant()));
    let total = 0, compareTotal = 0, count = 0;
    if (!baseInCart) { total += this.basePc; compareTotal += this.baseCc || this.basePc; count += 1; }
    if (this.itemsEl) {
      this.itemsEl.querySelectorAll('.r69pair__item').forEach((row) => {
        const cb = row.querySelector('.r69pair__cb');
        const inCart = this.cartSet.has(String(row.dataset.vid));
        const on = cb && cb.checked && !cb.disabled && !inCart;
        row.classList.toggle('is-off', !on && !inCart);
        if (on) {
          total += parseInt(row.dataset.pc, 10) || 0;
          compareTotal += parseInt(row.dataset.cc, 10) || parseInt(row.dataset.pc, 10) || 0;
          count += 1;
        }
      });
    }

    const totalEl = this.querySelector('[data-r69pair-total]');
    const countEl = this.querySelector('[data-r69pair-count]');
    const addLabel = this.querySelector('[data-r69pair-addlabel]');
    if (count === 0) {
      if (this.addBtn) this.addBtn.disabled = true;
      if (addLabel) addLabel.textContent = this.i18n.allInBag;
      if (totalEl) totalEl.textContent = ' ';
      if (countEl) countEl.textContent = this.i18n.complete;
      return;
    }
    if (this.addBtn) this.addBtn.disabled = false;
    const money = formatMoney(total, this.moneyFormat, this.currency);
    if (totalEl) totalEl.textContent = money;
    if (addLabel) addLabel.textContent = this.i18n.addPrice.replace('{amount}', money);
    if (countEl) countEl.textContent = `${count} ${count === 1 ? this.i18n.piece : this.i18n.pieces}`;
  }

  #addLook() {
    const items = [];
    const base = this.#liveBaseVariant();
    if (base && !this.cartSet.has(String(base))) items.push({ id: Number(base), quantity: 1 });
    if (this.itemsEl) {
      this.itemsEl.querySelectorAll('.r69pair__item').forEach((row) => {
        const cb = row.querySelector('.r69pair__cb');
        if (cb && cb.checked && !cb.disabled && !this.cartSet.has(String(row.dataset.vid))) items.push({ id: Number(row.dataset.vid), quantity: 1 });
      });
    }
    if (!items.length) return;
    this.#cartAdd(items, this.addBtn);
  }

  /* ---------- More to love (related) ---------- */
  #renderEdit(items) {
    const cap = items.length >= 8 ? 8 : items.length >= 4 ? 4 : items.length;
    const show = items.slice(0, cap);
    this.editRegion.setAttribute('data-count', String(show.length));
    // clear the reservation skeletons before inserting the real cards
    this.editGridEl.querySelectorAll('.r69pair__card--skel').forEach((el) => el.remove());
    const html = show.map((p, i) => {
      const n = String(i + 1).padStart(2, '0');
      const priceHtml = p.sale ? `<b class="r69pair__now">${esc(p.price)}</b> <s>${esc(p.cmp)}</s>` : `<b>${esc(p.price)}</b>`;
      const saleTag = `<span class="r69pair__cardsale"${p.sale ? '' : ' hidden'}>${esc(this.i18n.sale)}</span>`;
      const sizeSel = this.#editSizeHTML(p);
      const addBtn = p.avail
        ? `<button class="r69pair__quickadd" aria-label="${esc(p.t)}"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"><path d="M12 5v14M5 12h14"/></svg></button>`
        : `<span class="r69pair__soldout">${esc(this.i18n.soldout)}</span>`;
      // The card photo is a mini gallery: the lead image plus each variant's own photo (deduped),
      // swipeable natively on touch and draggable through the shared module on desktop
      // ([data-r69-gallery] is its opt-in hook). The FIRST img keeps data-r69pair-thumb so the
      // existing variant-select swap keeps working untouched.
      const galleryImgs = [p.img].concat((Array.isArray(p.vars) ? p.vars : []).map((v) => v.img))
        .filter(Boolean)
        .filter((src, i, arr) => arr.indexOf(src) === i)
        .slice(0, 5);
      const gallerySlides = galleryImgs
        .map((src, i) => `<img${i === 0 ? ' data-r69pair-thumb' : ''} src="${esc(src)}" alt="${i === 0 ? esc(p.t) : ''}" loading="lazy">`)
        .join('');
      return `
        <article class="r69pair__card${p.avail ? '' : ' is-soldout'}" data-vid="${esc(p.vid)}">
          <a class="r69pair__cardmedia" href="${esc(p.u)}">
            <div class="r69pair__cardgal"${galleryImgs.length > 1 ? ' data-r69-gallery' : ''}>${gallerySlides}</div>
            ${saleTag}
            <span class="r69pair__cardno">${n}</span>
            <span class="r69pair__cardbag" hidden><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M5 12l5 5L20 6"/></svg> ${esc(this.i18n.inbag)}</span>
          </a>
          <div class="r69pair__cardbody">
            <a class="r69pair__cardtitle" href="${esc(p.u)}">${esc(p.t)}</a>
            ${p.mat ? `<div class="r69pair__cardsub">${esc(p.mat)}</div>` : ''}
            ${sizeSel}
            <div class="r69pair__cardfoot">
              <span class="r69pair__price" data-r69pair-price>${priceHtml}</span>
              ${addBtn}
            </div>
          </div>
        </article>`;
    }).join('');

    this.editGridEl.insertAdjacentHTML('beforeend', html);
    this.editCards = Array.from(this.editGridEl.querySelectorAll('.r69pair__card'));
    this.editCards.forEach((card, idx) => {
      const p = show[idx];
      card._vars = Array.isArray(p.vars) ? p.vars : [];
      card._cpos = parseInt(p.cpos || 0, 10);
      card._spos = parseInt(p.spos || 0, 10);
      card._sname = p.sname || '';
      card.dataset.color = p.color == null ? '' : p.color;
      const sel = card.querySelector('.r69pair__size');
      if (sel) { sel.addEventListener('change', () => this.#onEditSize(card, sel)); this.#buildSkin(sel); }
      const btn = card.querySelector('.r69pair__quickadd');
      if (btn) btn.addEventListener('click', (e) => { e.preventDefault(); this.#cartAdd([{ id: Number(card.dataset.vid), quantity: 1 }], btn); });
    });

    this.#matchEdit(this.baseSize);
    this.#updateEditCart();
    this.editRegion.hidden = false;
    this.#syncRule();
  }

  #editSizeHTML(p) {
    if (!p.spos) return '';
    const seen = new Set();
    const rows = [];
    (p.vars || []).forEach((v) => {
      if (p.cpos && p.color && !eqv(v.c, p.color)) return;
      if (seen.has(String(v.s))) return;
      seen.add(String(v.s));
      rows.push(v);
    });
    if (rows.length <= 1) return '';
    const ring = isRing(rows.map((v) => v.s));
    const opts = rows.map((v) =>
      `<option value="${esc(v.id)}"${v.avail ? '' : ' disabled'}${String(v.id) === String(p.vid) ? ' selected' : ''}>${esc(this.#optLabel(v.s, ring, p.sname))}</option>`
    ).join('');
    return `<span class="r69pair__sizewrap"><select class="r69pair__size" aria-label="${esc(p.sname)}">${opts}</select></span>`;
  }

  #onEditSize(card, sel) {
    const opt = sel.options[sel.selectedIndex];
    if (!opt) return;
    const v = (card._vars || []).find((x) => String(x.id) === String(opt.value));
    if (v) this.#applyEditVariant(card, v, false);
    this.#updateSkinFromSelect(sel);
  }

  #matchEdit(size) {
    if (!this.editCards || !size) return;
    this.editCards.forEach((card) => {
      const vars = card._vars;
      if (!vars || !vars.length || !card._spos) return;
      const match = vars.find((v) => v.avail && eqv(v.s, size));
      if (!match || String(card.dataset.vid) === String(match.id)) return;
      this.#applyEditVariant(card, match, true);
    });
  }

  #applyEditVariant(card, v, rebuild) {
    card.dataset.vid = v.id;
    const priceEl = card.querySelector('[data-r69pair-price]');
    if (priceEl) priceEl.innerHTML = v.sale ? `<b class="r69pair__now">${esc(v.price)}</b> <s>${esc(v.cmp)}</s>` : `<b>${esc(v.price)}</b>`;
    const sale = card.querySelector('.r69pair__cardsale');
    if (sale) sale.hidden = !v.sale;
    const img = card.querySelector('[data-r69pair-thumb]');
    if (img && v.img && img.getAttribute('src') !== v.img) {
      img.style.opacity = '0';
      const pre = new Image();
      pre.onload = pre.onerror = () => { img.src = v.img; img.style.opacity = '1'; };
      pre.src = v.img;
    }
    const btn = card.querySelector('.r69pair__quickadd');
    if (btn) btn.disabled = !v.avail;
    card.classList.toggle('is-soldout', !v.avail);
    if (rebuild) this.#rebuildEditSize(card, v.id);
    this.#updateEditCard(card);
  }

  #rebuildEditSize(card, selectedVid) {
    const vars = card._vars || [];
    const color = card.dataset.color || '';
    const sname = card._sname || '';
    const seen = new Set();
    const rows = [];
    vars.forEach((v) => {
      if (card._cpos && color && !eqv(v.c, color)) return;
      if (seen.has(String(v.s))) return;
      seen.add(String(v.s));
      rows.push(v);
    });
    const ring = isRing(rows.map((v) => v.s));
    const html = rows.map((v) =>
      `<option value="${esc(v.id)}"${v.avail ? '' : ' disabled'}${String(v.id) === String(selectedVid) ? ' selected' : ''}>${esc(this.#optLabel(v.s, ring, sname))}</option>`
    ).join('');
    let sel = card.querySelector('.r69pair__size');
    if (!sel) {
      if (seen.size > 1) {
        const sub = card.querySelector('.r69pair__cardsub') || card.querySelector('.r69pair__cardtitle');
        if (sub) {
          sub.insertAdjacentHTML('afterend', `<span class="r69pair__sizewrap"><select class="r69pair__size" aria-label="${esc(sname)}">${html}</select></span>`);
          sel = card.querySelector('.r69pair__size');
          if (sel) { sel.addEventListener('change', () => this.#onEditSize(card, sel)); this.#buildSkin(sel); }
        }
      }
      return;
    }
    sel.innerHTML = html;
    const wrap = sel.closest('.r69pair__sizewrap');
    if (wrap) wrap.style.display = seen.size > 1 ? '' : 'none';
    this.#buildSkin(sel);
  }

  #updateEditCart() {
    if (this.editCards) this.editCards.forEach((card) => this.#updateEditCard(card));
  }

  #updateEditCard(card) {
    const inCart = this.cartSet.has(String(card.dataset.vid));
    card.classList.toggle('is-incart', inCart);
    const bag = card.querySelector('.r69pair__cardbag');
    if (bag) bag.hidden = !inCart;
  }

  /* ---------- Cart add (Horizon deferred-promise pattern -> drawer opens) ---------- */
  async #cartAdd(items, btn) {
    if (btn) btn.classList.add('is-loading');

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((el) => { if (el.dataset.sectionId) sectionIds.push(el.dataset.sectionId); });

    const totalQuantity = items.reduce((s, it) => s + it.quantity, 0);
    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((it) => ({ merchandiseId: String(it.id), quantity: it.quantity })),
      promise: deferred.promise,
    }));

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: items.map((it) => ({ id: Number(it.id), quantity: it.quantity })),
          sections: sectionIds.join(','),
        }),
      }).then((r) => r.json());

      if (btn) btn.classList.remove('is-loading');
      if (response && response.status) { deferred.reject(new Error(response.message || 'Add to cart failed')); return; }
      if (btn) { btn.classList.add('is-added'); setTimeout(() => btn.classList.remove('is-added'), 1600); }

      const cart = await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'r69-pairing',
          sourceId: this.id.toString(),
          itemCount: totalQuantity,
          productId: this.productId,
          sections: response.sections,
          didError: false,
        },
      });
      this.#applyCart(cart);
    } catch (e) {
      if (btn) btn.classList.remove('is-loading');
      deferred.reject(e);
    }
  }

  /* ---------- Current-product variant reactivity (base of the bundle) ---------- */
  #liveBaseVariant() {
    const input = document.querySelector(
      'product-form-component [name="id"], product-information [name="id"], form[action*="/cart/add"] [name="id"]'
    );
    return (input && input.value) || this.baseVid;
  }

  #bindBaseVariant() {
    let map = {};
    const el = this.querySelector('[data-r69pair-base]');
    if (el) { try { map = JSON.parse(el.textContent.trim()); } catch (e) { map = {}; } }
    this.baseMap = map;

    // Horizon fires shopify:product:select when the main picker changes; the hidden
    // [name="id"] updates only after the picker's fetch+morph, so re-read a few times.
    document.addEventListener(StandardEvents.productSelect, () => {
      this.#syncBase();
      [120, 350, 700].forEach((t) => setTimeout(() => this.#syncBase(), t));
    });
  }

  #syncBase() {
    const vid = String(this.#liveBaseVariant());
    if (!vid || vid === this.baseVid) return;
    const v = this.baseMap[vid];
    if (!v) return;
    const prevSize = this.baseSize;
    this.baseVid = vid;
    this.basePc = v.pc;
    this.baseCc = v.cc || v.pc;
    this.baseSize = v.size || '';
    const heroImg = this.querySelector('[data-r69pair-hero-img]');
    const heroPrice = this.querySelector('[data-r69pair-hero-price]');
    const heroSize = this.querySelector('[data-r69pair-hero-size]');
    if (heroImg && v.img) heroImg.src = v.img;
    if (heroPrice) heroPrice.innerHTML = v.sale ? `<b class="r69pair__now">${esc(v.price)}</b> <s>${esc(v.cmp)}</s>` : `<b>${esc(v.price)}</b>`;
    if (heroSize && v.size) heroSize.textContent = (heroSize.dataset.prefix ? heroSize.dataset.prefix + ': ' : '') + (this.baseRing ? ringLabel(v.size) : v.size);
    this.#updateBaseCart();
    // Only re-force paired/related items back to the base size when the base SIZE actually changed.
    // A colour/metal swap yields a new variant id (so the id guard above passes) but the same size —
    // without this gate it would overwrite a size the shopper picked manually in an item's own
    // dropdown, silently putting the wrong size in the cart.
    if (this.baseSize !== prevSize) {
      this.#matchComplementary(this.baseSize);
      this.#matchEdit(this.baseSize);
    }
    this.#recalc();
  }
}

if (!customElements.get('r69-pairing')) {
  customElements.define('r69-pairing', R69Pairing);
}
